import React, { useState } from "react";
import './CustomInput.scss'
import { useFormContext } from "react-hook-form";
import CustomButton from "../CustomButton/CustomButton";
import { Icons } from "../../assets/icons/icon";

interface Props {
    type: 'text' | 'password' | 'textarea' | 'number' | "file";
    label?: string;
    name: string;
    pattern?: RegExp;
    placeholder?: string;
    disabled?: boolean;
    required?: boolean;
    autoFocus?: boolean;
    fullWidth?: boolean;
    showBorder?: boolean
    width?: string;
    inputAdornment?: boolean;
    adornmentPosition?: 'start' | 'end';
    adornment?: React.ReactNode;
    isGreyBackground?: boolean;
    isMultiLine?: boolean;
    helperText?: string;
}

function CustomInput({
    label,
    name,
    disabled = false,
    placeholder = '',
    type,
    required = false,
    autoFocus = false,
    fullWidth = false,
    showBorder = false,
    width,
    inputAdornment,
    adornmentPosition,
    adornment,
    isGreyBackground = false,
    isMultiLine,
    helperText = ''
}: Readonly<Props>) {
    const {
        resetField,
        watch,
        register,
        formState: { errors }
    } = useFormContext();
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const error = errors[name];

    const handleTogglePasswordVisibility = () => {
        setShowPassword((prev) => !prev);
    };

    const getType = (): string => {
        if (type === "password") {
            if (showPassword) {
                return "text";
            } else {
                return "password";
            }
        } else {
            return type;
        }
    };

    const getClassName = (): string => {
        const classes = [];
        if (error) {
            classes.push("error");
        }
        if (type === "password" || showPassword) {
            classes.push("password");
        }
        if (disabled) {
            classes.push("disabled");
        }
        return classes.join(" ");
    };

    // Helper function to render the input element
    const renderInputElement = () => {
        if (isMultiLine) {
            return (
                <textarea
                    {...register(name)}
                    placeholder={placeholder}
                    className={`${getClassName()} ${showBorder && "show-border"} ${isGreyBackground ? "grey-bg" : "white-bg"}`}
                    style={{
                        width: width || "100%",
                        paddingLeft: inputAdornment && adornmentPosition === 'start' ? '3.5rem' : undefined,
                        paddingRight: inputAdornment && adornmentPosition === 'end' ? '2.5rem' : undefined
                    }}
                />
            );
        } else if (type === "file") {
            // Use register for file input and assign ref
            const { ref, ...fileInputProps } = register(name);
            console.log(watch(name))
            return (
                <div
                    className={`${getClassName()} ${showBorder && "show-border"} white-bg file-wrapper`}
                >
                    <div className="file-name">
                        <img src={Icons.ImageThumbnail} alt="Thumbnail" />
                        <div>{selectedFile?.name}</div>
                    </div>
                    <CustomButton
                        text={selectedFile === null ? "Select" : "Delete"}
                        width="100px"
                        height="36px"
                        background="#EBA83233"
                        onClick={() => {
                            if (selectedFile === null) {
                                // Ensure the ref is set and input is not disabled
                                if (fileInputRef.current && !fileInputRef.current.disabled) {
                                    fileInputRef.current.click();
                                } else {
                                    // fallback: try to find the input by id and click
                                    const input = document.getElementById(name) as HTMLInputElement | null;
                                    if (input && !input.disabled) input.click();
                                }
                            } else {
                                resetField(name);
                                setSelectedFile(null);
                            }
                        }}
                    />
                    <input
                        type="file"
                        hidden
                        id={name}
                        ref={e => {
                            if (ref) {
                                if (typeof ref === 'function') ref(e);
                                else if (typeof ref === 'object' && 'current' in ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = e;
                            }
                        }}
                        {...fileInputProps}
                        multiple={false}
                        accept="image/*"
                        onChange={e => {
                            const file = e.target.files?.[0] ?? null;
                            setSelectedFile(file);
                            if (fileInputProps.onChange) fileInputProps.onChange(e);
                        }}
                    />
                </div>
            );
        } else {
            return (
                <input
                    min={0}
                    type={getType()}
                    step={getType() === "number" ? "any" : undefined}
                    id={name}
                    placeholder={placeholder}
                    {...register(name)}
                    className={`${getClassName()} ${showBorder && "show-border"} ${isGreyBackground ? "grey-bg" : "white-bg"}`}
                    disabled={disabled}
                    autoFocus={autoFocus}
                    autoComplete="off"
                    readOnly
                    onFocus={(e) => e.target.removeAttribute("readOnly")}
                    onKeyDown={(e) => {
                        if (
                            getType() === "number" &&
                            ["e", "E", "+", "-"].includes(e.key)
                        ) {
                            e.preventDefault();
                        }
                        if (
                            type === 'password' &&
                            e.key === ' '
                        ) {
                            e.preventDefault();
                        }
                    }}
                    style={{
                        width: width || "100%",
                        paddingLeft: inputAdornment && adornmentPosition === 'start' ? '3.5rem' : undefined,
                        paddingRight: inputAdornment && adornmentPosition === 'end' ? '2.5rem' : undefined
                    }}
                />
            );
        }
    };

    return (
        <div className={`text-input-container ${fullWidth ? "full-width" : "input-width"}`}>
            {label && <label className="text-input-label">{label} {required && <span className="label-required">*</span>}</label>}
            <div className={`${type === "password" ? "input-field-password" : "input-fields"} ${isGreyBackground ? "grey-bg" : "white-bg"}`} style={{ width: fullWidth ? "100% !important" : "340px" }}>
                <div className="input-with-adornment">
                    {inputAdornment && adornmentPosition === 'start' && (
                        <span className="input-adornment start">{adornment}</span>
                    )}
                    {renderInputElement()}
                    {inputAdornment && adornmentPosition === 'end' && (
                        <span className="input-adornment end">{adornment}</span>
                    )}
                </div>
                {type === "password" &&
                    <button
                        className={`${showPassword ? "eye-on" : "eye-off"} not-a-button`}
                        onClick={handleTogglePasswordVisibility}
                    />
                }
            </div>
            {helperText && <div className="text-input-label">{helperText}</div>}
            {error?.message && <div className="error-message">{error?.message as string}</div>}
        </div>
    );
}

export default CustomInput;
